import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, Building2, Briefcase, Award, Target, Clock, 
  BarChart3, PieChart, Activity, Search, Filter, RefreshCw, Download,
  Eye, ChevronRight, Star, DollarSign, Calendar, CheckCircle, 
  AlertCircle, ArrowUpRight, ArrowDownRight, TrendingDown, Send,
  UserCheck, FileText, Phone, Mail, X, Edit
} from 'lucide-react';
import toast from 'react-hot-toast';
import CustomerManagement from './CustomerManagement';
import JobManagement from './JobManagement';
import Submissions from './Submissions';
import ClientSubmissionEditModal from '../components/ClientSubmissionEditModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const LeadSalesManagerDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [selectedSalesManager, setSelectedSalesManager] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [showEditSubmissionModal, setShowEditSubmissionModal] = useState(false);

  // Data states
  const [salesManagers, setSalesManagers] = useState([]);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    salesManager: 'all',
    client: 'all',
    dateRange: 'last_30_days',
    searchTerm: ''
  });

  // Metrics
  const [teamMetrics, setTeamMetrics] = useState({
    totalClients: 0,
    openRequisitions: 0,
    totalSubmissions: 0,
    totalInterviews: 0,
    totalOffers: 0,
    totalHires: 0,
    avgTimeToFill: 0,
    fillRate: 0,
    last7DaysSubmissions: 0,
    last30DaysSubmissions: 0,
    last90DaysSubmissions: 0
  });

  const [salesManagerPerformance, setSalesManagerPerformance] = useState([]);
  const [clientBreakdown, setClientBreakdown] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    calculateMetrics();
  }, [salesManagers, clients, jobs, submissions]);

  // Clear search terms when switching tabs
  useEffect(() => {
    setFilters(prev => ({ ...prev, searchTerm: '' }));
  }, [activeTab]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSalesManagers(),
        fetchClients(),
        fetchJobs(),
        fetchSubmissions()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesManagers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        const salesUsers = (data.users || []).filter(u => u.role === 'sales');
        setSalesManagers(salesUsers);
      }
    } catch (error) {
      console.error('Error fetching sales managers:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/customers`);
      if (response.ok) {
        const data = await response.json();
        setClients(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly
        const jobs = Array.isArray(data) ? data : (data.jobs || []);
        setJobs(jobs);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions`);
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const calculateMetrics = () => {
    // Total metrics
    const totalClients = clients.length;
    const openRequisitions = jobs.filter(j => j.status === 'open').length;
    const totalSubmissions = submissions.length;
    const totalInterviews = submissions.filter(s => 
      ['interview_scheduled', 'interview_scheduled', 'interview_scheduled'].includes(s.current_status)
    ).length;
    const totalOffers = submissions.filter(s => 
      ['offer_made', 'offer_accepted'].includes(s.current_status)
    ).length;
    const totalHires = submissions.filter(s => s.current_status === 'offer_accepted').length;

    // Time-based submissions
    const now = new Date();
    const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const last90Days = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

    const last7DaysSubmissions = submissions.filter(s => 
      new Date(s.submission_date) > last7Days
    ).length;
    const last30DaysSubmissions = submissions.filter(s => 
      new Date(s.submission_date) > last30Days
    ).length;
    const last90DaysSubmissions = submissions.filter(s => 
      new Date(s.submission_date) > last90Days
    ).length;

    // Average time to fill (simplified)
    const closedJobs = jobs.filter(j => j.status === 'closed' && j.closed_at && j.created_at);
    const avgTimeToFill = closedJobs.length > 0
      ? Math.round(
          closedJobs.reduce((acc, job) => {
            const days = Math.floor((new Date(job.closed_at) - new Date(job.created_at)) / (1000 * 60 * 60 * 24));
            return acc + days;
          }, 0) / closedJobs.length
        )
      : 0;

    // Fill rate
    const fillRate = openRequisitions > 0 
      ? ((totalHires / (openRequisitions + closedJobs.length)) * 100).toFixed(1)
      : 0;

    setTeamMetrics({
      totalClients,
      openRequisitions,
      totalSubmissions,
      totalInterviews,
      totalOffers,
      totalHires,
      avgTimeToFill,
      fillRate,
      last7DaysSubmissions,
      last30DaysSubmissions,
      last90DaysSubmissions
    });

    // Calculate Sales Manager performance
    calculateSalesManagerPerformance();
    calculateClientBreakdown();
  };

  const calculateSalesManagerPerformance = () => {
    const performance = salesManagers.map(sm => {
      // Get clients for this sales manager
      const smClients = clients.filter(c => 
        c.account_manager === sm.full_name || 
        c.account_manager === sm.username ||
        c.sales_rep === sm.full_name
      );

      // Get jobs for these clients
      const clientNames = smClients.map(c => c.company_name || c.client_name);
      const smJobs = jobs.filter(j => clientNames.includes(j.company));
      const openReqs = smJobs.filter(j => j.status === 'open').length;

      // Get submissions for these jobs
      const jobIds = smJobs.map(j => j.id);
      const smSubmissions = submissions.filter(s => jobIds.includes(s.job_id));
      
      const interviews = smSubmissions.filter(s => 
        ['interview_scheduled', 'interview_scheduled', 'interview_scheduled'].includes(s.current_status)
      ).length;
      
      const offers = smSubmissions.filter(s => 
        ['offer_made', 'offer_accepted'].includes(s.current_status)
      ).length;
      
      const hires = smSubmissions.filter(s => s.current_status === 'offer_accepted').length;

      // Calculate time to fill for closed jobs
      const closedJobs = smJobs.filter(j => j.status === 'closed' && j.closed_at && j.created_at);
      const timeToFill = closedJobs.length > 0
        ? Math.round(
            closedJobs.reduce((acc, job) => {
              const days = Math.floor((new Date(job.closed_at) - new Date(job.created_at)) / (1000 * 60 * 60 * 24));
              return acc + days;
            }, 0) / closedJobs.length
          )
        : 0;

      // Fill rate
      const totalReqs = smJobs.length;
      const fillRate = totalReqs > 0 ? ((hires / totalReqs) * 100).toFixed(1) : 0;

      return {
        id: sm.id,
        name: sm.full_name || sm.username,
        email: sm.email,
        clients: smClients.length,
        openReqs,
        submissions: smSubmissions.length,
        interviews,
        offers,
        hires,
        timeToFill,
        fillRate: parseFloat(fillRate),
        lastActivity: sm.last_login
      };
    });

    setSalesManagerPerformance(performance.sort((a, b) => b.submissions - a.submissions));
  };

  const calculateClientBreakdown = () => {
    // Deduplicate clients by company/client name (not ID) to prevent duplicate rows
    // Use Set to track seen names for better deduplication
    const seenNames = new Set();
    const uniqueClients = clients.filter(client => {
      const clientName = (client.company_name || client.client_name || '').trim().toLowerCase();
      if (!clientName || seenNames.has(clientName)) {
        return false;
      }
      seenNames.add(clientName);
      return true;
    });

    const breakdown = uniqueClients.map(client => {
      const clientName = client.company_name || client.client_name;
      
      // Get jobs for this client (case-insensitive match)
      const clientJobs = jobs.filter(j => 
        (j.company || '').trim().toLowerCase() === (clientName || '').trim().toLowerCase()
      );
      const openReqs = clientJobs.filter(j => j.status === 'open').length;
      
      // Get submissions for these jobs
      const jobIds = clientJobs.map(j => j.id);
      const clientSubmissions = submissions.filter(s => jobIds.includes(s.job_id));
      
      const interviews = clientSubmissions.filter(s => 
        ['interview_scheduled', 'interview_scheduled', 'interview_scheduled'].includes(s.current_status)
      ).length;
      
      const offers = clientSubmissions.filter(s => 
        ['offer_made', 'offer_accepted'].includes(s.current_status)
      ).length;
      
      const hires = clientSubmissions.filter(s => s.current_status === 'offer_accepted').length;

      return {
        id: client.id,
        name: clientName,
        accountManager: client.account_manager,
        openReqs,
        submissions: clientSubmissions.length,
        interviews,
        offers,
        hires,
        lastActivity: client.updated_at || client.created_at
      };
    });

    setClientBreakdown(breakdown.sort((a, b) => b.openReqs - a.openReqs));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Overview Tab
  const OverviewTab = () => {
    const [selectedMetric, setSelectedMetric] = useState(null);

    // Get all open jobs from sales team
    const teamJobs = jobs.filter(job => job.status === 'open');
    
    // Filter data based on selected metric
    const getFilteredData = () => {
      switch(selectedMetric) {
        case 'jobs':
          return teamJobs;
        case 'submissions':
          // Show submissions for all team jobs
          const teamJobIds = teamJobs.map(j => j.id);
          return submissions.filter(s => 
            teamJobIds.includes(s.job_id) && 
            s.status !== 'rejected' && 
            s.status !== 'offer_accepted'
          );
        case 'interviews':
          const teamJobIds2 = teamJobs.map(j => j.id);
          return submissions.filter(s => 
            teamJobIds2.includes(s.job_id) && 
            s.status === 'interview_scheduled'
          );
        case 'offers':
          const teamJobIds3 = teamJobs.map(j => j.id);
          return submissions.filter(s => 
            teamJobIds3.includes(s.job_id) && 
            (s.status === 'offer_made' || s.status === 'offer_extended')
          );
        case 'onboards':
          const teamJobIds4 = teamJobs.map(j => j.id);
          return submissions.filter(s => 
            teamJobIds4.includes(s.job_id) && 
            (s.status === 'offer_accepted' || s.status === 'onboarding')
          );
        default:
          return [];
      }
    };

    const filteredData = getFilteredData();

    // Calculate metrics for team's jobs
    const teamJobIds = teamJobs.map(j => j.id);
    const teamSubmissions = submissions.filter(s => teamJobIds.includes(s.job_id));
    const activeSubmissions = teamSubmissions.filter(s => 
      s.status !== 'rejected' && s.status !== 'offer_accepted'
    ).length;
    const interviewCount = teamSubmissions.filter(s => s.status === 'interview_scheduled').length;
    const offersCount = teamSubmissions.filter(s => 
      s.status === 'offer_made' || s.status === 'offer_extended'
    ).length;
    const onboardingCount = teamSubmissions.filter(s => 
      s.status === 'offer_accepted' || s.status === 'onboarding'
    ).length;

    return (
      <div className="space-y-6">
        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div 
            onClick={() => setSelectedMetric('jobs')}
            className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Open Requisitions</p>
                <p className="text-3xl font-bold mt-1">{teamJobs.length}</p>
              </div>
              <Briefcase className="h-12 w-12 text-orange-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('submissions')}
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Active Submissions</p>
                <p className="text-3xl font-bold mt-1">{activeSubmissions}</p>
              </div>
              <Send className="h-12 w-12 text-purple-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('interviews')}
            className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm font-medium">Current Interviews</p>
                <p className="text-3xl font-bold mt-1">{interviewCount}</p>
              </div>
              <Calendar className="h-12 w-12 text-indigo-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('offers')}
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Open Offers Made</p>
                <p className="text-3xl font-bold mt-1">{offersCount}</p>
              </div>
              <Award className="h-12 w-12 text-green-200" />
            </div>
          </div>

          <div 
            onClick={() => setSelectedMetric('onboards')}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Open Onboarding</p>
                <p className="text-3xl font-bold mt-1">{onboardingCount}</p>
              </div>
              <UserCheck className="h-12 w-12 text-blue-200" />
            </div>
          </div>
        </div>

        {/* Data Table - Shows when metric is clicked */}
        {selectedMetric && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedMetric === 'jobs' && 'Open Requisitions (Team-Wide)'}
                {selectedMetric === 'submissions' && 'Active Submissions (Team-Wide)'}
                {selectedMetric === 'interviews' && 'Candidates in Interviews (Team-Wide)'}
                {selectedMetric === 'offers' && 'Open Offers Made (Team-Wide)'}
                {selectedMetric === 'onboards' && 'Onboarding in Progress (Team-Wide)'}
              </h3>
              <button
                onClick={() => setSelectedMetric(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-x-auto">
              {selectedMetric === 'jobs' ? (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Manager</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submissions</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Posted</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map(job => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{job.title}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.client || job.client_name || job.company || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.account_manager || job.created_by_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.location || 'Remote'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            {job.status || 'Open'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submissions.filter(s => s.job_id === job.id).length}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Manager</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map(submission => {
                      const job = jobs.find(j => j.id === submission.job_id);
                      return (
                        <tr key={submission.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {submission.candidate_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {submission.job_title || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {job?.client || job?.client_name || job?.company || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {job?.account_manager || job?.created_by_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {(submission.current_status || submission.status)?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {submission.submitted_by_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <button 
                                onClick={() => setSelectedSubmission(submission)}
                                className="text-orange-600 hover:text-orange-700 flex items-center gap-1"
                                title="View Submission Details"
                              >
                                <Eye className="h-4 w-4" />
                                <span className="text-sm">View</span>
                              </button>
                              <button 
                                onClick={() => {
                                  setEditingSubmission(submission);
                                  setShowEditSubmissionModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                title="Edit Submission Status"
                              >
                                <Edit className="h-4 w-4" />
                                <span className="text-sm">Edit Status</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {filteredData.length === 0 && (
              <div className="p-12 text-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </div>
        )}

        {/* Sales Manager Performance Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-orange-600" />
              Sales Manager Performance
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Manager</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clients</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open Reqs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submissions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interviews</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Offers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time-to-Fill</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fill %</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {salesManagerPerformance.map(sm => (
                  <tr key={sm.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{sm.name}</p>
                        <p className="text-sm text-gray-500">{sm.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{sm.clients}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{sm.openReqs}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{sm.submissions}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{sm.interviews}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{sm.offers}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{sm.hires}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{sm.timeToFill}d</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        sm.fillRate >= 20 ? 'bg-green-100 text-green-800' :
                        sm.fillRate >= 10 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {sm.fillRate}%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setSelectedSalesManager(sm)}
                        className="text-orange-600 hover:text-orange-700"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {salesManagerPerformance.length === 0 && (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                      No sales managers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="h-12 w-12 bg-orange-600 rounded-full flex items-center justify-center text-white">
              <TrendingUp className="h-6 w-6" />
            </div>
            Lead Sales Manager Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Oversee sales team performance and client portfolios</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <button
            onClick={fetchAllData}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {[
              { key: 'overview', label: 'Overview', icon: Activity },
              { key: 'jobs', label: 'Job Management', icon: Briefcase },
              { key: 'submissions', label: 'Client Submissions', icon: Send },
              { key: 'customers', label: 'Customer Management', icon: Building2 }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-orange-600 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {loading && activeTab === 'overview' ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'overview' && <OverviewTab />}
              {activeTab === 'jobs' && <JobManagement />}
              {activeTab === 'submissions' && <Submissions />}
              {activeTab === 'customers' && <CustomerManagement />}
            </>
          )}
        </div>
      </div>

      {/* Client Submission Edit Modal */}
      {showEditSubmissionModal && editingSubmission && (
        <ClientSubmissionEditModal
          isOpen={showEditSubmissionModal}
          onClose={() => {
            setShowEditSubmissionModal(false);
            setEditingSubmission(null);
          }}
          submission={editingSubmission}
          onUpdate={(updatedSubmission) => {
            fetchSubmissions();
            setShowEditSubmissionModal(false);
            setEditingSubmission(null);
            toast.success('Submission status updated successfully');
          }}
        />
      )}
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color, trend, subtitle }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    indigo: 'from-indigo-500 to-indigo-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    teal: 'from-teal-500 to-teal-600',
    yellow: 'from-yellow-500 to-yellow-600',
    pink: 'from-pink-500 to-pink-600'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-lg p-6 text-white`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-white text-opacity-90 text-sm font-medium">{title}</p>
        <Icon className="h-8 w-8 text-white text-opacity-80" />
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {subtitle && <p className="text-white text-opacity-75 text-xs mt-1">{subtitle}</p>}
      {trend && <p className="text-white text-opacity-90 text-sm mt-2">{trend}</p>}
    </div>
  );
};

export default LeadSalesManagerDashboard;
